const pagesSettingsAuthenticationEn = {
  accessRules: {
    delete: {
      confirm: 'Delete',
      description: 'The targets of this access rule will no longer have the policy applied. This cannot be undone.',
      title: 'Delete this access rule?'
    },
    description: 'Configure login methods and authentication requirements per audience.',
    form: {
      applicationScope: {
        description: 'Limit this access rule to specific applications. When set, it only applies while issuing SSO for those applications. Leave it off to apply to sign-in and every application.',
        label: 'Application scope',
        placeholder: 'Select applications',
        restrictLabel: 'Limit to specific applications'
      },
      backToList: 'Back to list',
      createTitle: 'Create access rule',
      editTitle: 'Update access rule',
      incompleteSummary: 'Some required fields are missing. Check the highlighted steps.',
      incompleteToast: 'The form is incomplete. Check the steps that need attention.',
      membershipTypes: {
        description: 'Built-in targets used to migrate the previous settings.',
        label: 'Account type'
      },
      meta: {
        policy: 'Auth requirement: {{name}}',
        updated: 'Updated: {{date}}'
      },
      name: {
        label: 'Name',
        placeholder: 'e.g. Head office network'
      },
      noAttachablePolicy: 'Create a non-default authentication policy before attaching an access rule. The default policy applies to everyone and cannot be attached.',
      peopleGroups: {
        label: 'Select PeopleGroup',
        manageLink: 'Manage PeopleGroups',
        searchPlaceholder: 'Search PeopleGroup...',
        targetCount: '{{count}} people targeted',
        targetCountNote: 'Approximate total of each group size. People in multiple groups may be counted more than once.'
      },
      policy: {
        description: 'The default policy is not listed, because it already applies to everyone.',
        edit: 'Edit',
        label: 'Authentication policy',
        placeholder: 'Select a policy'
      },
      softwareNameLoadError: 'Could not resolve application names. Unresolved IDs are shown as-is.',
      stepTitles: {
        appScope: '4. Application scope',
        basic: '1. Basic information',
        peopleGroup: '2. People to apply this policy to (PeopleGroup)',
        policy: '3. Authentication policy to apply'
      },
      steps: {
        appScope: 'Application scope',
        basic: 'Basic information',
        peopleGroup: 'Select PeopleGroup',
        policy: 'Select authentication policy'
      },
      targets: {
        description: 'Select at least one target.',
        label: 'Targets'
      }
    },
    list: {
      applicationScopeAll: 'All applications',
      applicationScopeCount: '{{count}} application(s)',
      columns: {
        actions: 'Actions',
        applicationScope: 'Application scope',
        name: 'Name',
        policy: 'Authentication policy',
        targets: 'Targets'
      },
      create: 'Create access rule',
      empty: 'No access rules yet. Create one to apply a policy to a group.',
      loadError: 'Could not load access rules.',
      peopleGroupWithCount: '{{name}} ({{count}})'
    },
    title: 'Access rules',
    toast: {
      created: 'Created the access rule',
      deleted: 'Deleted the access rule',
      updated: 'Updated the access rule'
    }
  },
  authPolicies: {
    delete: {
      confirm: 'Delete',
      descriptionReferenced: 'Referenced by {{count}} access rule(s), which must be updated separately. This cannot be undone.',
      descriptionUnreferenced: 'This policy is not referenced by any access rule. This cannot be undone.',
      title: 'Delete this authentication policy?'
    },
    description: 'Define authentication requirement policies and apply them from access rules.',
    form: {
      accessConditions: 'Access conditions',
      backToList: 'Back to list',
      basicInfo: 'Basic information',
      createTitle: 'Create authentication policy',
      defaultPolicyNote: 'This is the default policy. Its status is fixed to Enforced and its fallback rule cannot deny access.',
      description: {
        label: 'Description',
        placeholder: 'What this policy is for'
      },
      editHeading: '{{name}} (authentication policy)',
      editTitle: 'Update authentication policy',
      meta: {
        attachmentCount: 'Applied by {{count}}',
        priority: 'Priority {{priority}}',
        updated: 'Updated {{date}}'
      },
      name: {
        label: 'Name',
        placeholder: 'e.g. SSO required'
      },
      rulesTitle: 'Rules',
      status: {
        label: 'Status'
      },
      stepTitles: {
        basic: '1. Basic information',
        conditions: '2. Access conditions'
      },
      steps: {
        basic: 'Basic information',
        conditions: 'Access conditions'
      },
      validateFailed: 'Could not validate the policy with the server. Check your connection and try again.'
    },
    list: {
      attachmentCountValue: 'Applied by {{count}}',
      columns: {
        actions: 'Actions',
        attachmentCount: 'Applied',
        name: 'Name',
        priority: 'Priority',
        ruleCount: 'Rules',
        status: 'Status'
      },
      create: 'Create policy',
      defaultBadge: 'Default',
      empty: 'No authentication policies yet.',
      loadError: 'Could not load authentication policies.',
      reorder: {
        cancel: 'Cancel',
        conflict: 'Another admin changed the order, so editing was canceled. Review the latest list and try again.',
        edit: 'Change order',
        save: 'Save order',
        staleDiscarded: 'A policy was added or removed, so reordering was canceled.'
      },
      ruleCountValue: '{{count}} rule(s)'
    },
    title: 'Authentication policies',
    toast: {
      created: 'Created the authentication policy',
      deleted: 'Deleted the authentication policy',
      reordered: 'Updated the priority order',
      updated: 'Updated the authentication policy'
    }
  },
  settings: {
    authenticationMethod: {
      description: 'Configure login requirements for each account type.',
      title: 'Login requirements'
    },
    navigation: {
      authenticationMethod: 'Login requirements',
      label: 'Settings',
      password: 'Password',
      session: 'Session rules',
      sso: 'SSO'
    },
    sso: {
      actions: {
        configure: 'Configure',
        edit: 'Edit'
      },
      card: {
        title: 'SSO delegation'
      },
      columns: {
        actions: 'Actions',
        delegationTarget: 'Authentication provider (one)',
        protocol: 'Protocol',
        provider: 'Authentication provider',
        status: 'Status'
      },
      delegationAriaLabel: 'Set {{provider}} as the delegation target',
      description: 'Select one provider to handle sign-in. Select DRESS CODE native when authentication should not be delegated to an external IdP.',
      disableDelegation: {
        blockedByFederatedOnly: 'Delegation cannot be cleared because Login Method Policy requires SSO only. Update Login Method Policy first.',
        confirm: 'Disable delegation',
        description: 'Stop delegating authentication to an external IdP and return to DRESS CODE native authentication. Confirm that users can sign in with a password or MFA before continuing.',
        title: 'Disable SSO delegation?'
      },
      heading: 'SSO — Authentication provider',
      loadError: 'Failed to load SSO settings.',
      native: {
        alwaysFallback: 'Always available',
        disableDelegationAriaLabel: 'Disable SSO delegation and use DRESS CODE native authentication',
        membershipIndividual: 'Member',
        membershipOperator: 'Staff',
        passwordAllowed: 'Password login: {{types}}',
        passwordDenied: 'Password login disabled',
        protocolMfa: 'MFA',
        protocolPassword: 'Password',
        title: 'DRESS CODE native'
      },
      retry: 'Retry',
      status: {
        activeDelegation: 'Active (delegated)',
        disabled: 'Disabled',
        nativeFallback: 'Active',
        notConfigured: 'Not configured'
      },
      title: 'SSO',
      toast: {
        connectionDeleted: 'SSO connection deleted',
        connectionSaved: 'SSO connection saved',
        delegationUpdated: 'SSO delegation updated'
      }
    }
  },
  tabs: {
    accessRules: 'Access rules',
    authPolicies: 'Authentication policies',
    settings: 'Settings'
  },
  title: 'Authentication'
};
export default pagesSettingsAuthenticationEn;